import { ExtractedData, ExtractionResult } from './onboarding-import.models';

/** The empty extraction payload every consumer defaults to. */
export const EMPTY_DATA: ExtractedData = {
  teams: [],
  shiftTemplates: [],
  employees: [],
  currentUserTeam: '',
  scheduleRecommendation: [],
};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const toRecord = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

// Pad an already-well-formed `H:mm` to `HH:mm` so a valid extraction result isn't
// rejected by the review step's time validator (which requires a zero-padded
// hour); a genuinely malformed value like '8am' is left for the validator to flag.
const padTime = (value: string): string => {
  const match = value.match(/^(\d{1,2}):(\d{2})$/);

  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : value;
};

/**
 * The result is LLM-derived and also re-read from sessionStorage, so its shape
 * is untrusted: honour the status the backend reported first, then default each
 * collection, coerce/trim string fields, pad times, and drop rows missing a
 * required field — rather than letting a null/missing array throw in a consumer.
 */
export function normaliseExtraction(raw: unknown): ExtractionResult {
  const record = toRecord(raw);

  // Respect a failure the backend already flagged; only derive empty/resolved
  // from the content when the call itself succeeded. Rewriting an 'error' to
  // 'empty'/'resolved' would either hide the read failure or walk the user into
  // review on a payload the backend rejected.
  if (trimmed(record['status']) === 'error') {
    return { status: 'error', data: EMPTY_DATA };
  }

  const source = toRecord(record['data']);

  const teams = asArray(source['teams'])
    .map((team) => ({ name: trimmed(toRecord(team)['name']) }))
    .filter((team) => team.name !== '');

  const shiftTemplates = asArray(source['shiftTemplates'])
    .map((shift) => {
      const row = toRecord(shift);

      return {
        name: trimmed(row['name']),
        startTime: padTime(trimmed(row['startTime'])),
        endTime: padTime(trimmed(row['endTime'])),
      };
    })
    .filter((shift) => shift.name !== '' && shift.startTime !== '' && shift.endTime !== '');

  const employees = asArray(source['employees'])
    .map((employee) => {
      const row = toRecord(employee);

      return {
        firstName: trimmed(row['firstName']),
        lastName: trimmed(row['lastName']),
        email: trimmed(row['email']) || undefined,
        team: trimmed(row['team']) || undefined,
      };
    })
    .filter((employee) => employee.firstName !== '' || employee.lastName !== '');

  // The recurring-schedule recommendation is also LLM-derived; keep only entries
  // with a team, a shift template and at least one day so the reconciler never
  // has to defend against a missing/non-array `days` from this source.
  const scheduleRecommendation = asArray(source['scheduleRecommendation'])
    .map((entry) => {
      const recommendation = toRecord(entry);
      const employeeName = trimmed(recommendation['employeeName']) || undefined;

      return {
        teamName: trimmed(recommendation['teamName']),
        shiftTemplateName: trimmed(recommendation['shiftTemplateName']),
        days: asArray(recommendation['days'])
          .map((day) => trimmed(day))
          .filter((day) => day !== ''),
        ...(employeeName ? { employeeName } : {}),
      };
    })
    .filter((entry) => entry.teamName !== '' && entry.shiftTemplateName !== '' && entry.days.length > 0);

  const data: ExtractedData = {
    teams,
    shiftTemplates,
    employees,
    currentUserTeam: trimmed(source['currentUserTeam']),
    scheduleRecommendation,
  };
  const hasContent = teams.length > 0 || shiftTemplates.length > 0 || employees.length > 0;

  return { status: hasContent ? 'resolved' : 'empty', data };
}
